// 🎯 BU KODU frontend/src/pages/MenuPage.js DOSYANIZA YAPIŞTIRIN

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Sun, Moon, Globe, ArrowLeft, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import WheelWidget from './wheel-widget';
import ProductBadge from '@/components/ProductBadge';
import '../wheel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const optimizeImageUrl = (url, width = 600) => {
  if (!url || !url.includes('/image/upload/')) {
    return url;
  }
  const transformations = `q_auto,f_auto,w_${width}`;
  return url.replace(
    '/image/upload/',
    `/image/upload/${transformations}/`
  );
};

const optimizeVideoUrl = (url, width = 500) => { // 🎯 Genişliği 500'de tutuyoruz
  if (!url || !url.includes('/video/upload/')) {
    return url;
  }
  const transformations = `vc_auto,q_auto,w_${width},c_limit`;
  return url.replace(
    '/video/upload/',
    `/video/upload/${transformations}/`
  );
};

// 🎯 YENİ: LazyVideo bileşeni buraya eklendi
const LazyVideo = ({ src, categoryId, className, style }) => {
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' } // Ekrana 50px yaklaşınca yüklemeye başla
    );

    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={videoRef} className={className} style={style}>
      {isVisible && src ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto" // 🎯 Görünür olduğunda yükle
          className="w-full h-full object-cover"
          style={{ borderRadius: '20px' }}
        >
          <source src={src} type="video/mp4" />
          Video desteklenmiyor.
        </video>
      ) : (
        // Video yüklenene kadar veya video yolu yoksa gösterilecek yer tutucu
        <div style={{ background: 'linear-gradient(135deg, #8b6f47, #6d5637)', width: '100%', height: '100%', borderRadius: '20px' }} />
      )}
    </div>
  );
};


const MenuPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const wheelWidgetRef = useRef(null);
  const [isWheelButtonExpanded, setIsWheelButtonExpanded] = useState(false);
  const categoryTabBarRef = useRef(null);

  // 🎯 KALDIRILDI: Video ve ref state'leri temizlendi.

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const categoriesRes = await axios.get(`${API}/menu/categories`);
      console.log('📦 Categories data:', categoriesRes.data); // Debug log
      categoriesRes.data.forEach(cat => {
        console.log(`Category: ${cat.name_tr}, Video: ${cat.video_path}, Image: ${cat.image_path}`);
      });
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(t('Menü yüklenirken hata oluştu', 'Error loading menu'));
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = async (category) => {
    // 🎯 Eğer ana menüdeyken kategori seçiliyorsa, ana menü scroll pozisyonunu kaydet
    if (!selectedCategory) {
      sessionStorage.setItem('main_menu_scroll', window.scrollY.toString());
    }
    
    // 🎯 Son seçilen kategoriyi kaydet (ana sayfaya dönerken kullanılacak)
    sessionStorage.setItem('last_selected_category', category.id);
    
    setSelectedCategory(category);
    setProductsLoading(true);
    setProducts([]);
    
    try {
      const res = await axios.get(`${API}/menu/products?category_id=${category.id}`);
      console.log('📦 Products data:', res.data); // Debug log
      res.data.forEach(prod => {
        console.log(`Product: ${prod.name_tr}, Image: ${prod.image_path}`);
      });
      setProducts(res.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('Ürünler yüklenirken hata oluştu', 'Error loading products'));
    } finally {
      setProductsLoading(false);
    }
  };

  // 🎯 Aktif kategoriyi tab bar'da görünür hale getir
  const scrollToActiveCategory = (categoryId) => {
    if (!categoryTabBarRef.current) return;
    
    const activeButton = categoryTabBarRef.current.querySelector(`[data-category-id="${categoryId}"]`);
    if (!activeButton) return;
    
    const container = categoryTabBarRef.current;
    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    
    // Butonun container içindeki pozisyonunu hesapla
    const buttonLeft = buttonRect.left - containerRect.left + container.scrollLeft;
    const buttonWidth = buttonRect.width;
    const containerWidth = containerRect.width;
    
    // Butonu ortaya getir
    const targetScrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
    
    container.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth'
    });
  };

  // 🎯 Kategori değiştiğinde tab bar'da otomatik scroll
  useEffect(() => {
    if (selectedCategory) {
      // DOM render tamamlandığında scroll yap
      const timer = setTimeout(() => {
        scrollToActiveCategory(selectedCategory.id);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedCategory]);

  // 🎯 Geri dönüldüğünde scroll pozisyonunu geri yükle
  useEffect(() => {
    if (!selectedCategory && !loading && categories.length > 0) {
      // Ana menü scroll pozisyonunu geri yükle
      const savedMainMenuPos = sessionStorage.getItem('main_menu_scroll');
      const lastSelectedCategoryId = sessionStorage.getItem('last_selected_category');
      
      if (savedMainMenuPos && lastSelectedCategoryId) {
        // Son seçilen kategoriye scroll yap
        const timer = setTimeout(() => {
          const categoryCard = document.querySelector(`[data-testid="category-card-${categories.findIndex(cat => cat.id === lastSelectedCategoryId)}"]`);
          if (categoryCard) {
            categoryCard.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          } else {
            // Kategori kartı bulunamazsa normal scroll pozisyonuna dön
            window.scrollTo({
              top: parseInt(savedMainMenuPos),
              behavior: 'smooth'
            });
          }
        }, 100);
        return () => clearTimeout(timer);
      } else if (savedMainMenuPos) {
        // Sadece scroll pozisyonu varsa normal geri yükleme
        const timer = setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedMainMenuPos),
            behavior: 'instant'
          });
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [selectedCategory, loading, categories]);

  const goBackToCategories = () => {
    setSelectedCategory(null);
    setProducts([]);
  };

  // 🎯 Logoya tıklandığında ana menünün en başına dön
  const goToMainMenuTop = () => {
    // Eğer kategori sayfasındaysak önce ana menüye dön
    if (selectedCategory) {
      // Ana menüye dönerken scroll pozisyonu kaydetme ve son kategori bilgisini temizle
      sessionStorage.removeItem('main_menu_scroll');
      sessionStorage.removeItem('last_selected_category');
      goBackToCategories();
    }
    
    // Kategori tab bar'ını da en başa scroll yap
    if (categoryTabBarRef.current) {
      categoryTabBarRef.current.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    }
    
    // Ana menünün en başına scroll yap
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, selectedCategory ? 100 : 0); // Kategori sayfasındaysak biraz bekle
  };

  /* 🎯 Auto-scroll helper */
  const handleGoToProduct = async (product) => {
    // 1. If category not selected or different, select it
    if (!selectedCategory || selectedCategory.id !== product.category_id) {
      const cat = categories.find(c => c.id === product.category_id);
      if (cat) {
        await selectCategory(cat);
      }
    }

    // 2. Wait a bit for render
    setTimeout(() => {
      const el = document.getElementById(`product-${product.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight effect
        el.style.transition = 'transform 0.5s, box-shadow 0.5s';
        el.style.transform = 'scale(1.05)';
        el.style.boxShadow = '0 0 20px var(--accent)';
        setTimeout(() => {
          el.style.transform = '';
          el.style.boxShadow = '';
        }, 1500);
      }
    }, 500);
  };

  const handleOpenWheel = () => {
    if (!wheelWidgetRef.current) {
      wheelWidgetRef.current = new WheelWidget({
        categories: categories,
        products: [], // We fetch on demand
        fetchProducts: async (catId) => {
          const res = await axios.get(`${API}/menu/products?category_id=${catId}`);
          return res.data;
        },
        onGoToProduct: handleGoToProduct
      });
    }
    // Update categories just in case
    wheelWidgetRef.current.data.categories = categories;
    wheelWidgetRef.current.data.onGoToProduct = handleGoToProduct;
    wheelWidgetRef.current.open(selectedCategory ? selectedCategory.id : null);
  };

  // 🎯 KALDIRILDI: Bu fonksiyonlara artık gerek yok.
  // const handleVideoError = (categoryId) => ...
  // const handleVideoLoad = (categoryId) => ...
  // const retryVideo = (categoryId) => ...

  const handleImageError = (productId) => {
    console.error(`Image error for product ${productId}`);
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  // 🎯 Enhanced Swipe gesture handling
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const isScrollingRef = useRef(false);
  const minSwipeDistance = 80; // Increased threshold
  const maxVerticalDeviation = 100; // Maximum vertical movement allowed
  const minSwipeVelocity = 0.3; // Minimum swipe velocity

  const onTouchStart = (e) => {
    // Only handle single touch
    if (e.touches.length !== 1) return;
    
    // Check if touch started in category tab bar area (avoid swipe in tab bar)
    const touch = e.touches[0];
    const tabBarHeight = 72 + 60; // navbar height + tab bar height
    if (touch.clientY < tabBarHeight) return;
    
    // Reset scrolling flag
    isScrollingRef.current = false;
    touchEndRef.current = null;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const onTouchMove = (e) => {
    // Only handle single touch
    if (e.touches.length !== 1) return;
    
    if (!touchStartRef.current) return;
    
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    
    // Check if user is scrolling vertically
    const deltaY = Math.abs(currentY - touchStartRef.current.y);
    const deltaX = Math.abs(currentX - touchStartRef.current.x);
    
    // If vertical movement is significant, consider it scrolling
    if (deltaY > 20 && deltaY > deltaX) {
      isScrollingRef.current = true;
    }
    
    touchEndRef.current = {
      x: currentX,
      y: currentY,
      time: Date.now()
    };
  };

  const onTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    
    // Don't trigger swipe if user was scrolling
    if (isScrollingRef.current) return;
    
    // Only trigger swipe if we're in a category (not on main menu)
    if (!selectedCategory) return;

    const distanceX = touchStartRef.current.x - touchEndRef.current.x;
    const distanceY = touchStartRef.current.y - touchEndRef.current.y;
    const timeElapsed = touchEndRef.current.time - touchStartRef.current.time;
    
    // Prevent very quick taps or very slow movements
    if (timeElapsed < 100 || timeElapsed > 1000) return;
    
    // Calculate swipe velocity
    const velocity = Math.abs(distanceX) / timeElapsed;
    
    // Check if it's a valid right swipe (sola doğru kaydırma = sağa swipe)
    const isRightSwipe = distanceX < -minSwipeDistance;
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY) * 2; // More strict horizontal requirement
    const hasMinimumVelocity = velocity > minSwipeVelocity;
    const verticalDeviationOk = Math.abs(distanceY) < maxVerticalDeviation;
    
    // Only trigger if all conditions are met
    if (isRightSwipe && isHorizontalSwipe && hasMinimumVelocity && verticalDeviationOk) {
      // Add a small delay to prevent accidental triggers
      setTimeout(() => {
        goBackToCategories();
      }, 50);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>{t('Menü yükleniyor...', 'Loading menu...')}</p>
        </div>
      </div>
    );
  }

  // Tema durumuna göre arka plan görüntüsünü seç
  const getBackgroundImage = () => {
    if (!selectedCategory) return null;
    return selectedCategory.image_path;
  };

  const backgroundImage = getBackgroundImage();

  return (
    <div
      className="min-h-screen relative"
      style={{ color: 'var(--text-primary)', background: 'var(--bg-primary)' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Background handling - Sabit arka plan görüntüsü (scroll sırasında yukarıda sabit kalır) */}
      {backgroundImage ? (
        <div
          className="absolute top-0 left-0 right-0 z-0 overflow-hidden"
          style={{
            height: '50vh',
            background: 'var(--bg-primary)',
            willChange: 'transform'
          }}
        >
          {/* Blurred Background Layer - Tüm alanı doldurur ve kalan yerleri kapatır */}
          <div
            className="absolute inset-0 bg-cover bg-center blur-2xl scale-125"
            style={{
              backgroundImage: `url(${optimizeImageUrl(backgroundImage, 400)})`,
              opacity: theme === 'dark' ? 0.4 : 0.5
            }}
          />

          {/* Main Image - Cover ile tüm alanı kaplar */}
          <img
            src={optimizeImageUrl(backgroundImage, 1200)}
            alt="Category Background"
            className="absolute inset-0 w-full h-full object-cover z-10"
            style={{ objectPosition: 'center center' }}
          />

          {/* Overlay - Tema bazlı örtü (Koyu modda siyah, açık modda beyaz) */}
          <div
            className={`absolute inset-0 z-20 pointer-events-none ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}
            style={{
              opacity: theme === 'dark' ? 0.50 : 0.35
            }}
          />

          {/* Bottom Gradient Fade - Arka planla yumuşak geçiş */}
          <div
            className="absolute bottom-0 left-0 right-0 h-32 z-20 pointer-events-none"
            style={{
              background: `linear-gradient(to top, var(--bg-primary) 0%, var(--bg-primary) 30%, transparent 100%)`
            }}
          />
        </div>
      ) : (
        <div className="fixed inset-0 z-0" style={{ background: 'var(--bg-primary)' }} />
      )}

      {/* Content wrapper to ensure z-index above background */}
      <div className="relative z-10">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 glass" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'row' }}
            >
              <img
                src={theme === 'dark' ? "/logo-dark.png" : "/logo.png"}
                alt="Bozs Logo"
                onClick={goToMainMenuTop}
                style={{
                  height: '40px',
                  width: '40px',
                  objectFit: 'contain',
                  flexShrink: 0,
                  display: 'block',
                  cursor: 'pointer'
                }}
              />
              <h1
                onClick={goToMainMenuTop}
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  margin: 0,
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  cursor: 'pointer'
                }}
              >
                BOZS
              </h1>
            </div>
            <div className="flex items-center gap-3">

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                className="rounded-full"
                data-testid="language-toggle"
                style={{ color: 'var(--text-primary)' }}
              >
                <Globe className="h-5 w-5" />
                <span className="ml-1 text-sm font-semibold">{language.toUpperCase()}</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
                style={{ color: 'var(--text-primary)' }}
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </header>

        {/* Category Tab Bar - Sticky horizontal scroll */}
        <div 
          ref={categoryTabBarRef}
          className="fixed left-0 right-0 z-40 glass overflow-x-auto scrollbar-hide"
          style={{ 
            top: '72px', // navbar height
            borderBottom: '1px solid var(--border)',
            background: theme === 'dark' ? 'rgba(45, 31, 20, 0.95)' : 'rgba(245, 241, 235, 0.95)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2 px-4 py-3 min-w-max sm:px-6">
            {categories.map((category) => {
              const isActive = selectedCategory?.id === category.id;
              return (
                <button
                  key={category.id}
                  data-category-id={category.id}
                  onClick={() => {
                    if (selectedCategory?.id !== category.id) {
                      // Kategori değiştirilirken ana menü scroll pozisyonunu koru
                      // (Zaten selectCategory fonksiyonu içinde ana menü pozisyonu kaydediliyor)
                      selectCategory(category);
                    }
                  }}
                  className="relative px-4 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all duration-300 hover:scale-105 sm:px-5"
                  style={{
                    background: isActive 
                      ? 'linear-gradient(135deg, #8b6f47 0%, #6d5637 100%)'
                      : theme === 'dark' 
                        ? 'rgba(139, 111, 71, 0.15)' 
                        : 'rgba(139, 111, 71, 0.08)',
                    color: isActive 
                      ? '#ffffff' 
                      : 'var(--text-primary)',
                    border: isActive 
                      ? '2px solid #8b6f47' 
                      : '2px solid transparent',
                    boxShadow: isActive 
                      ? '0 4px 12px rgba(139, 111, 71, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
                      : 'none',
                    transform: isActive ? 'translateY(-1px)' : 'none'
                  }}
                >
                  {/* Shine effect for active tab */}
                  {isActive && (
                    <span 
                      className="absolute inset-0 rounded-full opacity-30"
                      style={{
                        background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                        animation: 'shine 2s ease-in-out infinite'
                      }}
                    />
                  )}
                  <span className="relative z-10">
                    {language === 'tr' ? category.name_tr : category.name_en}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Hero Section */}
        <section className={`${selectedCategory ? 'pt-56' : 'pt-36'} pb-12 px-4`}>
          {/* ... (içerik aynı) ... */}
          <div className="container mx-auto text-center">
            {selectedCategory ? (
              <div>
                <Button
                  variant="ghost"
                  onClick={goBackToCategories}
                  className="mb-6 flex items-center gap-2 mx-auto"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <ArrowLeft className="h-5 w-5" />
                  {t('Kategorilere Dön', 'Back to Categories')}
                </Button>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Georgia, serif' }}>
                  {language === 'tr' ? selectedCategory.name_tr : selectedCategory.name_en}
                </h2>

              </div>
            ) : (
              <div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Georgia, serif' }}>
                  {t('Coffee & Bistro Menümüz', 'Our Coffee & Bistro Menu')}
                </h2>
                <p className="text-base sm:text-lg" style={{ color: 'var(--text-secondary)', fontFamily: 'Verdana, sans-serif' }}>
                  {t('Lezzetli kahve ve bistro deneyimi', 'Delicious coffee and bistro experience')}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* 🎯 GÜNCELLENDİ: Kategori kartları artık LazyVideo bileşenini kullanıyor */}
        {
          !selectedCategory && (
            <section className="pb-12 px-4">
              <div className="container mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((category, idx) => (
                    <div
                      key={category.id}
                      className="video-card aspect-video fade-in"
                      style={{ animationDelay: `${idx * 0.1}s`, borderRadius: '20px' }}
                      onClick={() => selectCategory(category)}
                      data-testid={`category-card-${idx}`}
                    >
                      {/* 🎯 GÜNCELLENDİ: Tüm eski video/hata/placeholder mantığı bu tek bileşenle değiştirildi */}
                      <LazyVideo
                        key={category.id}
                        src={category.video_path ? optimizeVideoUrl(category.video_path) : null}
                        categoryId={category.id}
                        className="w-full h-full"
                        style={{ borderRadius: '20px' }}
                      />

                      <div className="video-card-overlay">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                            {language === 'tr' ? category.name_tr : category.name_en}
                          </h3>

                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )
        }

        {/* Products (değişiklik yok) */}
        {
          selectedCategory && (
            <section className="pb-16 px-4">
              <div className="container mx-auto">
                {productsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mx-auto" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
                    <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>{t('Ürünler yükleniyor...', 'Loading products...')}</p>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-lg" style={{ color: 'var(--text-secondary)', fontFamily: 'Verdana, sans-serif' }}>
                      {t('Bu kategoride henüz ürün bulunmuyor.', 'No products found in this category.')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {products.map((product, idx) => (
                      <div
                        key={product.id}
                        id={`product-${product.id}`}
                        className="overflow-hidden fade-in group hover-scale-bounce shine-effect relative"
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border)',
                          boxShadow: '0 2px 8px var(--shadow)',
                          animationDelay: `${idx * 0.05}s`,
                          borderRadius: '16px'
                        }}
                        data-testid={`product-card-${idx}`}
                      >

                        {/* Product Image */}
                        {product.image_path && !imageErrors[product.id] && (
                          <div className="w-full h-48 overflow-hidden">
                            <img
                              src={optimizeImageUrl(product.image_path, 600)}
                              alt={language === 'tr' ? product.name_tr : product.name_en}
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                              onError={() => handleImageError(product.id)}
                              loading="lazy"
                            />
                          </div>
                        )}

                        {/* Fallback */}
                        {product.image_path && imageErrors[product.id] && (
                          <div
                            className="w-full h-48 flex items-center justify-center"
                            style={{
                              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                              color: '#6b7280'
                            }}
                          >
                            <div className="text-center">
                              <div className="text-3xl mb-2">🍽️</div>
                              <p className="text-sm">Fotoğraf yüklenemedi</p>
                            </div>
                          </div>
                        )}

                        {/* Product Content */}
                        <div className="p-6">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-semibold mb-2 group-hover:text-[var(--accent)] transition-colors" style={{ color: 'var(--text-primary)', fontFamily: 'Georgia, serif' }}>
                                {language === 'tr' ? product.name_tr : product.name_en}
                              </h3>
                              {(language === 'tr' ? product.description_tr : product.description_en) && (
                                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'Verdana, sans-serif' }}>
                                  {language === 'tr' ? product.description_tr : product.description_en}
                                </p>
                              )}
                              {/* YENİ: Ürün Rozeti - Normal flow içinde, açıklama gibi */}
                              <ProductBadge badgeType={product.badge_type} />
                            </div>
                            <div className="text-right">
                              <span className="price-tag inline-block text-lg">
                                {product.price.toFixed(2)} {language === 'tr' ? '₺' : 'TRY'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )
        }

        {/* Footer (değişiklik yok) */}
        <footer className="py-8 px-4" style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
          <div className="container mx-auto text-center" style={{ color: 'var(--text-muted)', fontFamily: 'Verdana, sans-serif' }}>
            <p>&copy; 2018 Bozs Cafe & Bistro. {t('Tüm hakları saklıdır.', 'All rights reserved.')}</p>
            <p className="mt-2 text-sm opacity-75">
              Created by <a href="https://www.instagram.com/recre_studio?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent)] transition-colors underline decoration-dotted">ReCre Studio</a>
            </p>
          </div>
        </footer>

        {/* Floating Action Button for Wheel of Fortune */}
        <button
          onClick={() => {
            if (isWheelButtonExpanded) {
              handleOpenWheel();
            } else {
              setIsWheelButtonExpanded(true);
            }
          }}
          className={`fixed bottom-6 right-6 z-50 bg-amber-500 hover:bg-amber-400 text-black shadow-2xl flex items-center justify-center transition-all duration-500 ease-out group ${isWheelButtonExpanded
            ? 'px-5 py-3 rounded-full'
            : 'w-14 h-14 rounded-full animate-bounce-slight'
            }`}
          title={t('Şans Çarkı', 'Wheel of Fortune')}
          style={{
            boxShadow: '0 4px 14px rgba(0,0,0,0.4), 0 0 20px rgba(245, 158, 11, 0.3)'
          }}
        >
          {/* Pulse Effect */}
          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-20 animate-ping"></span>

          <div className="flex items-center overflow-hidden">
            {/* Icon */}
            <span className={`text-2xl transition-transform duration-700 ease-in-out ${isWheelButtonExpanded ? 'rotate-[360deg]' : ''}`}>
              🎡
            </span>

            {/* Text */}
            <span
              className={`font-bold text-sm whitespace-nowrap font-sans transition-all duration-500 ease-in-out ${isWheelButtonExpanded ? 'max-w-xs opacity-100 ml-2' : 'max-w-0 opacity-0'
                }`}
            >
              {t('Bugün Ne Yesem?', 'What Should I Eat?')}
            </span>
          </div>
        </button>
      </div >
    </div >
  );
};

export default MenuPage;